/**
 * NATEX Score View - UX Overhaul
 *
 * Bloomberg-style terminal with hero score display
 * Large typography, clear hierarchy
 */

import React, { useMemo, useState } from 'react';
import {
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Star,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  Info,
  Database,
  Wifi,
  WifiOff,
  Clock,
} from 'lucide-react';
import { Tooltip } from '../components/ui/Tooltip';

// Import scoring module
import {
  calculateNatexScore,
  getCountryAdjustments,
  ScoreSignal,
  TrendDirection,
  Pillar,
  NatexScore,
  TOTAL_INDICATORS,
} from '../services/scoring';

// Import live data hook
import { useNatexLiveData, DataSourceStatus } from '../hooks/useNatexLiveData';

// Score Hero Panel - GIANT score display
const ScoreHeroPanel: React.FC<{
  score: NatexScore;
  dataStatus: DataSourceStatus;
}> = ({ score, dataStatus }) => {
  const getSignalColor = (signal: ScoreSignal) => {
    switch (signal) {
      case ScoreSignal.STRONG_BUY: return 'data-green';
      case ScoreSignal.BUY: return 'green-400';
      case ScoreSignal.HOLD: return 'data-amber';
      case ScoreSignal.UNDERWEIGHT: return 'orange-400';
      case ScoreSignal.AVOID: return 'data-red';
    }
  };

  const formatSignal = (signal: ScoreSignal) => signal.replace('_', ' ');

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden">
      <div className="p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Left: GIANT Score */}
          <div className="flex-1 flex flex-col items-center lg:items-start">
            <div className="text-xs text-data-amber uppercase tracking-widest font-semibold mb-4">
              NATEX Opportunity Score
            </div>

            {/* Giant Score Number */}
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-8xl lg:text-9xl font-mono font-black text-data-amber leading-none">
                {Math.round(score.score)}
              </span>
              <span className="text-3xl lg:text-4xl font-mono text-gray-500">/100</span>
            </div>

            {/* Signal Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-${getSignalColor(score.signal)}/20 border border-${getSignalColor(score.signal)}/30`}>
              <Star size={18} className={`text-${getSignalColor(score.signal)} fill-${getSignalColor(score.signal)}`} />
              <span className={`text-lg font-bold text-${getSignalColor(score.signal)}`}>
                {formatSignal(score.signal)}
              </span>
            </div>

            {/* Stars */}
            <div className="flex items-center gap-1 mt-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  size={20}
                  className={i < score.stars ? 'text-data-amber fill-data-amber' : 'text-gray-600'}
                />
              ))}
              <span className="ml-2 text-sm text-gray-400">{score.stars}/5 Stars</span>
            </div>
          </div>

          {/* Right: Quick Stats */}
          <div className="w-full lg:w-72 space-y-4">
            {/* Country */}
            <div className="bg-terminal-bg/60 rounded-xl p-4 border border-terminal-border/50 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-data-amber to-data-green flex items-center justify-center mb-2">
                <span className="text-black font-black text-xl">{score.countryCode.slice(0, 2)}</span>
              </div>
              <div className="text-lg font-bold text-white">{score.countryName}</div>
              <div className="text-xs text-gray-500">Central Asia's Rising Power</div>
            </div>

            {/* Score Change */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
                <div className={`text-2xl font-mono font-bold ${
                  score.scoreChange > 0 ? 'text-data-green' : score.scoreChange < 0 ? 'text-data-red' : 'text-gray-400'
                }`}>
                  {score.scoreChange > 0 ? '+' : ''}{score.scoreChange.toFixed(1)}
                </div>
                <div className="text-[10px] text-gray-500">vs Previous</div>
              </div>
              <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
                <div className="text-2xl font-mono font-bold text-data-blue">{score.grade}</div>
                <div className="text-[10px] text-gray-500">Grade</div>
              </div>
            </div>

            {/* Trend */}
            <div className="flex items-center justify-center gap-2 py-2 bg-terminal-bg/40 rounded-lg">
              {score.trend === TrendDirection.RISING ? (
                <TrendingUp size={16} className="text-data-green" />
              ) : score.trend === TrendDirection.FALLING ? (
                <TrendingDown size={16} className="text-data-red" />
              ) : (
                <Minus size={16} className="text-gray-400" />
              )}
              <span className="text-sm text-gray-300">{score.trend} Trend</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Pillar Breakdown Panel - Horizontal bars
const PillarBreakdownPanel: React.FC<{ score: NatexScore }> = ({ score }) => {
  const [expandedPillar, setExpandedPillar] = useState<Pillar | null>(null);

  const getScoreColor = (s: number) => {
    if (s >= 80) return 'data-green';
    if (s >= 60) return 'data-amber';
    return 'data-red';
  };

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-bg/30">
        <div className="flex items-center gap-2">
          <Target size={18} className="text-data-blue" />
          <h2 className="text-base font-bold text-white uppercase tracking-wide">Pillar Breakdown</h2>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {score.pillars.map((pillar) => (
          <div key={pillar.pillar}>
            <button
              onClick={() => setExpandedPillar(expandedPillar === pillar.pillar ? null : pillar.pillar)}
              className="w-full"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-white">{pillar.name}</span>
                  <span className="text-xs text-gray-500">({Math.round(pillar.weight * 100)}%)</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xl font-mono font-bold text-${getScoreColor(pillar.score)}`}>
                    {Math.round(pillar.score)}
                  </span>
                  {expandedPillar === pillar.pillar ? (
                    <ChevronUp size={14} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={14} className="text-gray-400" />
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="h-3 bg-terminal-border rounded-full overflow-hidden">
                <div
                  className={`h-full bg-${getScoreColor(pillar.score)} rounded-full transition-all duration-500`}
                  style={{ width: `${pillar.score}%` }}
                />
              </div>
            </button>

            {/* Expanded Factors */}
            {expandedPillar === pillar.pillar && (
              <div className="mt-3 ml-4 space-y-2 border-l-2 border-terminal-border pl-4">
                {pillar.factors.map((factor) => (
                  <div key={factor.factor} className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{factor.name}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-terminal-border rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-${getScoreColor(factor.score)} rounded-full`}
                          style={{ width: `${factor.score}%` }}
                        />
                      </div>
                      <span className={`text-xs font-mono font-semibold text-${getScoreColor(factor.score)} w-6 text-right`}>
                        {Math.round(factor.score)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Key Drivers Panel - Strengths & Weaknesses
const KeyDriversPanel: React.FC<{ score: NatexScore }> = ({ score }) => {
  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-bg/30">
        <div className="flex items-center gap-2">
          <TrendingUp size={18} className="text-data-green" />
          <h2 className="text-base font-bold text-white uppercase tracking-wide">Key Drivers</h2>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Strengths */}
        <div>
          <h3 className="text-xs text-data-green font-semibold uppercase tracking-wider mb-3">
            Strengths
          </h3>
          <div className="space-y-2">
            {score.strengths.slice(0, 4).map((strength, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-data-green shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300">{strength}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Weaknesses */}
        <div>
          <h3 className="text-xs text-data-red font-semibold uppercase tracking-wider mb-3">
            Risks
          </h3>
          <div className="space-y-2">
            {score.weaknesses.slice(0, 4).map((weakness, i) => (
              <div key={i} className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-data-red shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300">{weakness}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Data Sources Panel
const DataSourcesPanel: React.FC<{
  liveCount: number;
  pendingCount: number;
  indicatorsWithData: number;
  totalIndicators: number;
  lastUpdated: Date | null;
  onRefresh: () => void;
  isLoading: boolean;
}> = ({ liveCount, pendingCount, indicatorsWithData, totalIndicators, lastUpdated, onRefresh, isLoading }) => {
  const sources = [
    { name: 'World Bank', status: 'live', color: 'data-green' },
    { name: 'IMF WEO', status: 'live', color: 'data-green' },
    { name: 'CBU', status: 'live', color: 'data-green' },
    { name: 'ADB', status: 'cached', color: 'data-blue' },
    { name: 'UNCTAD', status: 'cached', color: 'data-amber' },
  ];

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-bg/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database size={18} className="text-data-blue" />
          <h2 className="text-base font-bold text-white uppercase tracking-wide">Data Sources</h2>
        </div>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="p-1.5 hover:bg-terminal-border rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={`text-gray-400 hover:text-white ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Data Completeness */}
        <div className="bg-terminal-bg/60 rounded-lg p-4 border border-terminal-border/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-400 uppercase">Score Based On</span>
            <span className="text-xl font-mono font-bold text-white">
              {indicatorsWithData}<span className="text-gray-500">/{totalIndicators}</span>
            </span>
          </div>
          <div className="h-2 bg-terminal-border rounded-full overflow-hidden">
            <div
              className="h-full bg-data-blue rounded-full"
              style={{ width: `${(indicatorsWithData / totalIndicators) * 100}%` }}
            />
          </div>
          <div className="text-[10px] text-gray-500 mt-2">indicators with verified data</div>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-2 bg-data-green/10 rounded-lg border border-data-green/20">
            <Wifi size={14} className="text-data-green" />
            <span className="text-sm text-data-green font-semibold">{liveCount} Live</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-data-amber/10 rounded-lg border border-data-amber/20">
            <Clock size={14} className="text-data-amber" />
            <span className="text-sm text-data-amber font-semibold">{pendingCount} Pending</span>
          </div>
        </div>

        {/* Source List */}
        <div className="space-y-2">
          {sources.map((source) => (
            <div key={source.name} className="flex items-center justify-between text-xs">
              <span className="text-gray-400">{source.name}</span>
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full bg-${source.color} ${source.status === 'live' ? 'animate-pulse' : ''}`} />
                <span className={`text-${source.color}`}>{source.status}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Last Updated */}
        {lastUpdated && (
          <div className="pt-3 border-t border-terminal-border/50 text-center">
            <span className="text-[10px] text-gray-500">
              Last fetched: {lastUpdated.toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Country Selector
const CountrySelector: React.FC<{
  selected: string;
  onChange: (code: string) => void;
}> = ({ selected, onChange }) => {
  const countries = [
    { code: 'UZB', name: 'Uzbekistan' },
    { code: 'KAZ', name: 'Kazakhstan' },
    { code: 'KGZ', name: 'Kyrgyzstan' },
    { code: 'TJK', name: 'Tajikistan' },
    { code: 'AZE', name: 'Azerbaijan' },
    { code: 'GEO', name: 'Georgia' },
    { code: 'MNG', name: 'Mongolia' },
  ];

  return (
    <select
      value={selected}
      onChange={(e) => onChange(e.target.value)}
      className="bg-terminal-border text-white text-sm rounded px-3 py-1.5 border border-terminal-border focus:outline-none focus:border-data-blue"
    >
      {countries.map((c) => (
        <option key={c.code} value={c.code}>{c.name}</option>
      ))}
    </select>
  );
};

// Main View Component
const NatexScoreView: React.FC = () => {
  const [selectedCountry, setSelectedCountry] = useState('UZB');

  const {
    countryData,
    isLoading,
    overallStatus,
    liveCount,
    pendingAssessmentCount,
    pendingDataCount,
    lastUpdated,
    refetch,
    indicatorsWithData,
    totalIndicators,
  } = useNatexLiveData(selectedCountry);

  const score = useMemo(() => {
    if (!countryData || Object.keys(countryData.indicators).length === 0) {
      return null;
    }

    const adjustments = getCountryAdjustments(selectedCountry);

    return calculateNatexScore({
      countryData,
      adjustments,
      previousScore: selectedCountry === 'UZB' ? 78 : undefined,
    });
  }, [countryData, selectedCountry]);

  if (isLoading && !score) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-terminal-bg">
        <RefreshCw size={32} className="text-data-blue animate-spin mb-4" />
        <span className="text-gray-400">Loading live data...</span>
      </div>
    );
  }

  if (!score) {
    return (
      <div className="flex-1 flex items-center justify-center bg-terminal-bg">
        <span className="text-gray-500">No data available</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-terminal-bg">
      {/* Header */}
      <div className="px-5 py-4 border-b border-terminal-border bg-terminal-header flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Target size={20} className="text-data-blue" />
          <h1 className="text-lg font-bold text-white">NATEX Opportunity Score</h1>
        </div>
        <div className="flex items-center gap-3">
          <CountrySelector selected={selectedCountry} onChange={setSelectedCountry} />
          <Tooltip content="Refresh data" position="bottom">
            <button
              onClick={refetch}
              disabled={isLoading}
              className="p-2 hover:bg-terminal-border rounded transition-colors disabled:opacity-50"
            >
              <RefreshCw size={16} className={`text-gray-400 hover:text-white ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {/* Hero Section */}
        <div className="mb-6">
          <ScoreHeroPanel score={score} dataStatus={overallStatus} />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
          {/* Pillar Breakdown - 2 columns */}
          <div className="lg:col-span-2">
            <PillarBreakdownPanel score={score} />
          </div>

          {/* Right Column */}
          <div className="space-y-4 lg:space-y-6">
            <KeyDriversPanel score={score} />
            <DataSourcesPanel
              liveCount={liveCount}
              pendingCount={pendingAssessmentCount + pendingDataCount}
              indicatorsWithData={indicatorsWithData}
              totalIndicators={totalIndicators}
              lastUpdated={lastUpdated}
              onRefresh={refetch}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Methodology Note */}
        <div className="mt-6 bg-terminal-panel/50 border border-terminal-border rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-gray-500 shrink-0 mt-0.5" />
            <div className="text-xs text-gray-500">
              <strong className="text-gray-400">Methodology:</strong> {score.methodology}.
              Score aggregates {TOTAL_INDICATORS} indicators across 5 pillars with weights:
              Growth (30%), Climate (25%), Catalyst (20%), Risk (15%), Valuation (10%).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NatexScoreView;
